import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface AdminUser {
  id: string;
  username: string;
  email?: string;
  role: string;
  created_at: string;
  post_count: number;
  reputation: number;
}

export const useAdminUsers = () => {
  return useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => {
      // Get users with their roles
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select(`
          id,
          display_name,
          created_at
        `)
        .order('created_at', { ascending: false });

      if (profilesError) throw profilesError;

      // Get user roles
      const { data: userRoles, error: rolesError } = await supabase
        .from('user_roles')
        .select('user_id, role');

      if (rolesError) throw rolesError;

      // Get post counts for each user (using user_id, not author_id)
      const { data: postCounts, error: postsError } = await supabase
        .from('posts')
        .select('user_id')
        .not('user_id', 'is', null);

      if (postsError) throw postsError;

      // Count posts per user
      const postCountMap = new Map<string, number>();
      postCounts?.forEach((post: any) => {
        if (post.user_id) {
          postCountMap.set(post.user_id, (postCountMap.get(post.user_id) || 0) + 1);
        }
      });

      // Create role map
      const roleMap = new Map<string, string>();
      userRoles?.forEach(userRole => {
        roleMap.set(userRole.user_id, userRole.role);
      });

      // Combine data (email retrieval disabled - requires additional setup)
      const users: AdminUser[] = profiles?.map((profile: any) => ({
        id: profile.id,
        username: profile.display_name || 'User',
        email: undefined,
        role: roleMap.get(profile.id) || 'user',
        created_at: profile.created_at || '',
        post_count: postCountMap.get(profile.id) || 0,
        reputation: 0,
      })) || [];

      return users;
    },
  });
};